// Player access menu component for the library

import React from 'react';
import { BanIcon, ChevronRightIcon, DoorOpen, LinkIcon } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
} from '../../../components/ui/popover';
import { Separator } from '../../../components/ui/separator';
import { Switch } from '../../../components/ui/switch';
import type { PlayerAccessViewModel } from '../hooks';
import BlockedUsersSyncIssueDialog from './BlockedSyncAlertDialog';

type PlayerAccessMenuProps = {
  viewModel: PlayerAccessViewModel;
};

const PlayerAccessMenu: React.FC<PlayerAccessMenuProps> = ({ viewModel }) => {
  return (
    <>
      <BlockedUsersSyncIssueDialog
        open={viewModel.syncIssueOpen}
        onOpenChange={viewModel.onSyncIssueOpenChange}
        onRetry={viewModel.onRetrySync}
        onReconnect={viewModel.onReconnectTwitch}
      />
      <PopoverHeader>
        <PopoverTitle>Player Access</PopoverTitle>
        <PopoverDescription>
          Control who can join your game rooms.
        </PopoverDescription>
      </PopoverHeader>
      <Separator />
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">Require Twitch Login</div>
            <p className="text-xs text-muted-foreground">
              Players must authenticate with Twitch to join game rooms.
            </p>
          </div>
          <Switch
            checked={viewModel.requireTwitchLoginChecked}
            onCheckedChange={viewModel.onRequireTwitchLoginToggle}
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">
              Disallow Blocked &amp; Banned
            </div>
            <p className="text-xs text-muted-foreground">
              Carry your Twitch moderation lists into game rooms. Must enable
              Require Twitch Login.
            </p>
          </div>
          <Switch
            checked={viewModel.blockedAndBannedChecked}
            onCheckedChange={viewModel.onBlockedAndBannedToggle}
          />
        </div>
        {viewModel.showConnectTwitchRow && (
          <Button
            variant="ghost"
            className="h-auto w-full justify-start gap-2 px-2 py-2 text-left"
            onClick={viewModel.onConnectTwitchRowClick}
          >
            <LinkIcon />
            <span>Connect Twitch</span>
            <ChevronRightIcon className="ml-auto text-muted-foreground" />
          </Button>
        )}
        {viewModel.showBlockedUsersRow && (
          <Button
            variant="ghost"
            className="h-auto w-full justify-start gap-2 px-2 py-2 text-left"
            onClick={viewModel.onBlockedUsersRowClick}
          >
            <BanIcon />
            <span>Blocked users</span>
            <Badge variant="secondary" className="ml-auto">
              {viewModel.blockedUsersBadgeLabel}
            </Badge>
            <ChevronRightIcon className="text-muted-foreground" />
          </Button>
        )}
      </div>
    </>
  );
};

export const PLAYER_ACCESS_MENU_ICON = <DoorOpen />;
export default PlayerAccessMenu;
